import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { groupFloorplansByBeds } from '../lib/floorplans';
import type { Floorplan, BedroomCategory } from '../lib/floorplans';

// ============================================================================
// This section is driven by the `floorplans` collection. Individual units are
// grouped by bedroom count (Studio / 1 Bedroom / …) and each group becomes a
// tab. The layout is unchanged from the previous static version — only the
// data source is different:
//   • tab label + panel heading  ← the group's name
//   • panel image                ← interior apartment photo (photo_gallery)
//   • "View Availability" CTA    ← deep link to /floor-plans?bedroom=<key>
//   • panel body copy            ← AI generator field (no per-plan prose field
//                                   exists in the model; pricing/availability
//                                   intentionally omitted — shown via AptSync
//                                   on the floor plans page)
// ============================================================================

/** Tabs are capped to preserve the original 4-tab layout. Adjust if needed. */
const MAX_PANELS = 4;

/** Deep link to the floor plans page for a given bedroom group. */
function floorPlansHref(cat: BedroomCategory | undefined): string {
  return cat ? `/floor-plans?bedroom=${cat.param}` : '/floor-plans';
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const panel1ImageDef: SlotGroupDefinition = {
  label: 'Floor Plan 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a bright interior photo of an apartment home (living room, kitchen, or bedroom). Prefer a real unit photo over a floor-plan drawing or exterior shot.',
    context:
      'Image for the 1st (smallest) bedroom-group tab in the "Find Your Space" section. Each tab must show a different interior photo.',
    sourceCollection: 'photo_gallery',
  },
};

const panel2ImageDef: SlotGroupDefinition = {
  label: 'Floor Plan 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a bright interior photo of an apartment home (living room, kitchen, or bedroom). Prefer a real unit photo over a floor-plan drawing or exterior shot.',
    context:
      'Image for the 2nd bedroom-group tab in the "Find Your Space" section. Each tab must show a different interior photo.',
    sourceCollection: 'photo_gallery',
  },
};

const panel3ImageDef: SlotGroupDefinition = {
  label: 'Floor Plan 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a bright interior photo of an apartment home (living room, kitchen, or bedroom). Prefer a real unit photo over a floor-plan drawing or exterior shot.',
    context:
      'Image for the 3rd bedroom-group tab in the "Find Your Space" section. Each tab must show a different interior photo.',
    sourceCollection: 'photo_gallery',
  },
};

const panel4ImageDef: SlotGroupDefinition = {
  label: 'Floor Plan 4 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[6].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 6,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a bright interior photo of an apartment home (living room, kitchen, or bedroom). Prefer a real unit photo over a floor-plan drawing or exterior shot.',
    context:
      'Image for the 4th (largest) bedroom-group tab in the "Find Your Space" section. Each tab must show a different interior photo.',
    sourceCollection: 'photo_gallery',
  },
};

// CTA slot groups keep the button label editable; the href is always the
// per-group deep link, so no editable `url` field is exposed.
const panel1CtaDef: SlotGroupDefinition = {
  label: 'Floor Plan 1 CTA',
  fields: {
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Availability' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel2CtaDef: SlotGroupDefinition = {
  label: 'Floor Plan 2 CTA',
  fields: {
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Availability' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel3CtaDef: SlotGroupDefinition = {
  label: 'Floor Plan 3 CTA',
  fields: {
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Availability' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel4CtaDef: SlotGroupDefinition = {
  label: 'Floor Plan 4 CTA',
  fields: {
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Availability' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

function ArrowIcon({ dir }: { dir: 'prev' | 'next' }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {dir === 'prev' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
    </svg>
  );
}

export function TabbedCallouts() {
  const floorplans = useCollectionBinding<Floorplan>('tabbedcallouts.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  const panelCount = Math.min(categories.length, MAX_PANELS);

  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = panelCount > 0 ? Math.min(activeIndex, panelCount - 1) : 0;

  const goPrev = () => {
    if (panelCount > 0) setActiveIndex((safeIndex - 1 + panelCount) % panelCount);
  };
  const goNext = () => {
    if (panelCount > 0) setActiveIndex((safeIndex + 1) % panelCount);
  };

  const img1 = useSlotGroup('tabbedcallouts.panel1_image', panel1ImageDef);
  const img2 = useSlotGroup('tabbedcallouts.panel2_image', panel2ImageDef);
  const img3 = useSlotGroup('tabbedcallouts.panel3_image', panel3ImageDef);
  const img4 = useSlotGroup('tabbedcallouts.panel4_image', panel4ImageDef);

  const cta1 = useSlotGroup('tabbedcallouts.panel1_cta', panel1CtaDef);
  const cta2 = useSlotGroup('tabbedcallouts.panel2_cta', panel2CtaDef);
  const cta3 = useSlotGroup('tabbedcallouts.panel3_cta', panel3CtaDef);
  const cta4 = useSlotGroup('tabbedcallouts.panel4_cta', panel4CtaDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  const tabClass = (i: number) =>
    `cta-tab-btn font-heading font-semibold tracking-tight lg:tracking-widest whitespace-nowrap uppercase transition-opacity duration-300 text-xs lg:text-sm ${
      i >= panelCount ? 'hidden ' : ''
    }${safeIndex === i ? 'text-primary-foreground opacity-100' : 'text-primary-foreground opacity-50 hover:opacity-80'}`;

  const ctaClass =
    'tabbed-cta group/cta relative inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 lg:px-10 lg:py-4 lg:text-base uppercase tracking-wider overflow-hidden transition-all duration-300';
  const headingClass =
    'font-heading text-2xl md:text-2xl font-normal text-primary-foreground leading-tight mb-5 md:mb-2 lg:mb-3';
  const bodyClass =
    'font-body font-light text-base text-primary-foreground opacity-80 leading-relaxed mb-5 md:mb-3 lg:mb-5 max-w-xl [&_a]:underline';

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-primary)] pt-10 pb-8 md:pt-20 md:pb-12"
    >
      <div className="px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-stretch">

          {/* ── Left column: heading + active panel text ── */}
          <div className="flex flex-col justify-start lg:pt-9">
            <RichTextContent
              path="tabbedcallouts.section_heading"
              label="Section Heading"
              defaultValue="<p>Find Your Space</p>"
              as="h2"
              className="font-heading text-3xl md:text-3xl lg:text-5xl font-normal text-primary-foreground leading-[1.05] uppercase mb-5 md:mb-2 lg:mb-4 [&_a]:underline"
            />

            {panelCount === 0 && (
              <p className="font-body font-light text-base text-primary-foreground opacity-80 leading-relaxed max-w-xl">
                Floor plan options will appear here.
              </p>
            )}

            {/* Panel 1 text */}
            <div className={safeIndex === 0 && panelCount > 0 ? 'block' : 'hidden'}>
              <h3 className={headingClass}>{categories[0]?.label ?? ''}</h3>
              <RichTextContent
                path="tabbedcallouts.panel1_body"
                label="Floor Plan 1 Body Text"
                defaultValue='<p>Thoughtfully designed homes at <span data-type="field-token" data-field="property_name"></span> pair modern finishes with an open, light-filled layout — a fully-equipped kitchen, generous storage, and comfortable living space built for everyday life.</p>'
                as="div"
                className={bodyClass}
              />
              <EditableSlotGroup groupPath="tabbedcallouts.panel1_cta" definition={panel1CtaDef}>
                <a href={floorPlansHref(categories[0])} target={cta1._target || '_self'} className={ctaClass}>
                  <span className="relative z-10">{cta1.label || 'View Availability'}</span>
                </a>
              </EditableSlotGroup>
            </div>

            {/* Panel 2 text */}
            <div className={safeIndex === 1 && panelCount > 1 ? 'block' : 'hidden'}>
              <h3 className={headingClass}>{categories[1]?.label ?? ''}</h3>
              <RichTextContent
                path="tabbedcallouts.panel2_body"
                label="Floor Plan 2 Body Text"
                defaultValue='<p>Every residence at <span data-type="field-token" data-field="property_name"></span> is crafted for comfort, with a spacious living area, a modern kitchen, and large windows that fill the home with natural light.</p>'
                as="div"
                className={bodyClass}
              />
              <EditableSlotGroup groupPath="tabbedcallouts.panel2_cta" definition={panel2CtaDef}>
                <a href={floorPlansHref(categories[1])} target={cta2._target || '_self'} className={ctaClass}>
                  <span className="relative z-10">{cta2.label || 'View Availability'}</span>
                </a>
              </EditableSlotGroup>
            </div>

            {/* Panel 3 text */}
            <div className={safeIndex === 2 && panelCount > 2 ? 'block' : 'hidden'}>
              <h3 className={headingClass}>{categories[2]?.label ?? ''}</h3>
              <RichTextContent
                path="tabbedcallouts.panel3_body"
                label="Floor Plan 3 Body Text"
                defaultValue='<p>Room to live and unwind at <span data-type="field-token" data-field="property_name"></span> — well-proportioned living spaces, premium finishes, and smart storage come together in a layout that feels like home.</p>'
                as="div"
                className={bodyClass}
              />
              <EditableSlotGroup groupPath="tabbedcallouts.panel3_cta" definition={panel3CtaDef}>
                <a href={floorPlansHref(categories[2])} target={cta3._target || '_self'} className={ctaClass}>
                  <span className="relative z-10">{cta3.label || 'View Availability'}</span>
                </a>
              </EditableSlotGroup>
            </div>

            {/* Panel 4 text */}
            <div className={safeIndex === 3 && panelCount > 3 ? 'block' : 'hidden'}>
              <h3 className={headingClass}>{categories[3]?.label ?? ''}</h3>
              <RichTextContent
                path="tabbedcallouts.panel4_body"
                label="Floor Plan 4 Body Text"
                defaultValue='<p>Elevated living at <span data-type="field-token" data-field="property_name"></span>, featuring a bright, open floor plan, a modern kitchen, and the quality details that define the community.</p>'
                as="div"
                className={bodyClass}
              />
              <EditableSlotGroup groupPath="tabbedcallouts.panel4_cta" definition={panel4CtaDef}>
                <a href={floorPlansHref(categories[3])} target={cta4._target || '_self'} className={ctaClass}>
                  <span className="relative z-10">{cta4.label || 'View Availability'}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* ── Right column: active panel image with prev/next arrows, tabs below ── */}
          <div className="flex flex-col">
            <div className="relative flex-1 md:flex-none lg:flex-1 min-h-[190px] md:min-h-0 md:h-[320px] lg:h-auto lg:min-h-[400px]">
            {/* Panel 1 image */}
            <EditableSlotGroup
              groupPath="tabbedcallouts.panel1_image"
              definition={panel1ImageDef}
              className={safeIndex === 0 && panelCount > 0 ? 'absolute inset-0' : 'hidden'}
            >
              {safeIndex === 0 && panelCount > 0 ? (
                img1.image ? (
                  <VibesitesImage
                    url={img1.image}
                    alt={img1.alt || (categories[0]?.label ? `${categories[0].label} apartment interior` : 'Apartment interior')}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )
              ) : (
                <span className="hidden" aria-hidden="true" />
              )}
            </EditableSlotGroup>

            {/* Panel 2 image */}
            <EditableSlotGroup
              groupPath="tabbedcallouts.panel2_image"
              definition={panel2ImageDef}
              className={safeIndex === 1 && panelCount > 1 ? 'absolute inset-0' : 'hidden'}
            >
              {safeIndex === 1 && panelCount > 1 ? (
                img2.image ? (
                  <VibesitesImage
                    url={img2.image}
                    alt={img2.alt || (categories[1]?.label ? `${categories[1].label} apartment interior` : 'Apartment interior')}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )
              ) : (
                <span className="hidden" aria-hidden="true" />
              )}
            </EditableSlotGroup>

            {/* Panel 3 image */}
            <EditableSlotGroup
              groupPath="tabbedcallouts.panel3_image"
              definition={panel3ImageDef}
              className={safeIndex === 2 && panelCount > 2 ? 'absolute inset-0' : 'hidden'}
            >
              {safeIndex === 2 && panelCount > 2 ? (
                img3.image ? (
                  <VibesitesImage
                    url={img3.image}
                    alt={img3.alt || (categories[2]?.label ? `${categories[2].label} apartment interior` : 'Apartment interior')}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )
              ) : (
                <span className="hidden" aria-hidden="true" />
              )}
            </EditableSlotGroup>

            {/* Panel 4 image */}
            <EditableSlotGroup
              groupPath="tabbedcallouts.panel4_image"
              definition={panel4ImageDef}
              className={safeIndex === 3 && panelCount > 3 ? 'absolute inset-0' : 'hidden'}
            >
              {safeIndex === 3 && panelCount > 3 ? (
                img4.image ? (
                  <VibesitesImage
                    url={img4.image}
                    alt={img4.alt || (categories[3]?.label ? `${categories[3].label} apartment interior` : 'Apartment interior')}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )
              ) : (
                <span className="hidden" aria-hidden="true" />
              )}
            </EditableSlotGroup>

            {/* Prev / Next arrows — only meaningful with more than one tab */}
            {panelCount > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous floor plan"
                  className="cta-nav-arrow absolute left-2 translate-x-0 md:left-0 md:-translate-x-1/2 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 md:h-14 md:w-14 items-center justify-center rounded-full"
                >
                  <ArrowIcon dir="prev" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next floor plan"
                  className="cta-nav-arrow absolute right-2 translate-x-0 md:right-0 md:translate-x-1/2 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 md:h-14 md:w-14 items-center justify-center rounded-full"
                >
                  <ArrowIcon dir="next" />
                </button>
              </>
            )}
            </div>

            {/* ── Tab Bar (below image) ── */}
            <div
              role="tablist"
              className="mt-6 md:mt-5 lg:mt-8 flex flex-nowrap lg:flex-wrap justify-between lg:justify-center gap-x-2 md:gap-x-1 lg:gap-x-6 gap-y-3"
            >
          <button
            type="button"
            role="tab"
            aria-selected={safeIndex === 0}
            onClick={() => setActiveIndex(0)}
            className={tabClass(0)}
          >
            {categories[0]?.label ?? ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={safeIndex === 1}
            onClick={() => setActiveIndex(1)}
            className={tabClass(1)}
          >
            {categories[1]?.label ?? ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={safeIndex === 2}
            onClick={() => setActiveIndex(2)}
            className={tabClass(2)}
          >
            {categories[2]?.label ?? ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={safeIndex === 3}
            onClick={() => setActiveIndex(3)}
            className={tabClass(3)}
          >
            {categories[3]?.label ?? ''}
          </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Richtext links inherit text-on-primary instead of the global
           --vibe-color-hyperlink token (which reads poorly on the brand stripe). */
        [data-vibesite-slot="TabbedCallouts"] [data-vibesite-richtext] a,
        [data-vibesite-slot="TabbedCallouts"] [data-vibesite-richtext] a:hover,
        [data-vibesite-slot="TabbedCallouts"] [data-vibesite-richtext] a:visited {
          color: inherit !important;
        }
        /* Outlined pill CTA — tracks the property's textOnPrimary token
           against the primary brand background. */
        .tabbed-cta {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 75%, transparent);
          border-radius: 9999px;
          color: var(--vibe-color-text-on-primary, #fff);
          box-shadow: none;
        }
        .tabbed-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 18%, transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .tabbed-cta:hover {
          border-color: var(--vibe-color-text-on-primary, #fff);
          color: var(--vibe-color-text-on-primary, #fff);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 18%, transparent);
        }
        .tabbed-cta:hover::before {
          opacity: 1;
        }
        .tabbed-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Circular prev/next navigation arrows over the image */
        .cta-nav-arrow {
          background: color-mix(in srgb, var(--vibe-color-primary, #000) 55%, transparent);
          border: 1.5px solid var(--vibe-color-text-on-primary, #fff);
          color: var(--vibe-color-text-on-primary, #fff);
          transition: background 0.25s ease, transform 0.2s ease;
        }
        .cta-nav-arrow:hover {
          background: var(--vibe-color-text-on-primary, #fff);
          color: var(--vibe-color-primary, #000);
        }
        .cta-nav-arrow:active {
          filter: brightness(0.95);
        }
      `}</style>

    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'TabbedCallouts',
  displayName: 'Tabbed Callouts',
  tokens: {
    // Section heading
    'tabbedcallouts.section_heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your Space</p>',
    },
    // Panel body copy — no per-plan prose field exists in the property model, so
    // these are AI-generated per property/floor plan. The defaults double as the
    // non-AI fallback and the company-theme preview. Pricing/availability are
    // intentionally omitted (surfaced via AptSync on the floor plans page).
    'tabbedcallouts.panel1_body': {
      label: 'Floor Plan 1 Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Thoughtfully designed homes at <span data-type="field-token" data-field="property_name"></span> pair modern finishes with an open, light-filled layout — a fully-equipped kitchen, generous storage, and comfortable living space built for everyday life.</p>',
      generator: {
        prompt:
          'Write a short description of the floor plan layout shown on this tab, focusing on the living space and everyday lifestyle. Do not mention pricing or availability.',
        context:
          'Body copy for one tab of the "Find Your Space" floor-plan section. Tabs show the property\'s floor plan options ordered smallest to largest; this is the 1st (smallest) option.',
        length: '2-3 sentences',
      },
    },
    'tabbedcallouts.panel2_body': {
      label: 'Floor Plan 2 Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Every residence at <span data-type="field-token" data-field="property_name"></span> is crafted for comfort, with a spacious living area, a modern kitchen, and large windows that fill the home with natural light.</p>',
      generator: {
        prompt:
          'Write a short description of the floor plan layout shown on this tab, focusing on the living space and everyday lifestyle. Do not mention pricing or availability.',
        context:
          'Body copy for one tab of the "Find Your Space" floor-plan section. Tabs show the property\'s floor plan options ordered smallest to largest; this is the 2nd option.',
        length: '2-3 sentences',
      },
    },
    'tabbedcallouts.panel3_body': {
      label: 'Floor Plan 3 Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Room to live and unwind at <span data-type="field-token" data-field="property_name"></span> — well-proportioned living spaces, premium finishes, and smart storage come together in a layout that feels like home.</p>',
      generator: {
        prompt:
          'Write a short description of the floor plan layout shown on this tab, focusing on the living space and everyday lifestyle. Do not mention pricing or availability.',
        context:
          'Body copy for one tab of the "Find Your Space" floor-plan section. Tabs show the property\'s floor plan options ordered smallest to largest; this is the 3rd option.',
        length: '2-3 sentences',
      },
    },
    'tabbedcallouts.panel4_body': {
      label: 'Floor Plan 4 Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Elevated living at <span data-type="field-token" data-field="property_name"></span>, featuring a bright, open floor plan, a modern kitchen, and the quality details that define the community.</p>',
      generator: {
        prompt:
          'Write a short description of the floor plan layout shown on this tab, focusing on the living space and everyday lifestyle. Do not mention pricing or availability.',
        context:
          'Body copy for one tab of the "Find Your Space" floor-plan section. Tabs show the property\'s floor plan options ordered smallest to largest; this is the 4th (largest) option.',
        length: '2-3 sentences',
      },
    },
  },
  slotGroups: {
    'tabbedcallouts.panel1_image': panel1ImageDef,
    'tabbedcallouts.panel1_cta': panel1CtaDef,
    'tabbedcallouts.panel2_image': panel2ImageDef,
    'tabbedcallouts.panel2_cta': panel2CtaDef,
    'tabbedcallouts.panel3_image': panel3ImageDef,
    'tabbedcallouts.panel3_cta': panel3CtaDef,
    'tabbedcallouts.panel4_image': panel4ImageDef,
    'tabbedcallouts.panel4_cta': panel4CtaDef,
  },
  collectionBindings: {
    'tabbedcallouts.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(TabbedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TabbedCallouts;
